import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * ScrollToTop — manages scroll position on route changes.
 *
 * Two cases:
 *
 *  1. No hash (e.g. /catering, /order, /): scroll to top so the new
 *     page's title and first paragraph are visible.
 *
 *  2. Hash present (e.g. /#menu, /#location, /#contact): scroll to the
 *     element with that id. We do this in a rAF (and fall back to a
 *     short timeout) because when navigating from /catering to /#menu,
 *     the landing page hasn't mounted yet at the moment this effect
 *     runs — the target element doesn't exist until after React paints.
 *
 * `behavior: 'smooth'` matches the browser's default behavior for
 * same-page anchor clicks, so cross-route anchor clicks feel the same
 * as anchor clicks on the landing page itself.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation()

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0)
      return
    }

    // Strip the leading '#' and look up the target.
    const id = hash.slice(1)
    if (!id) {
      window.scrollTo(0, 0)
      return
    }

    // Try immediately, then on next frame, then after a short delay.
    // The first attempt that finds the element wins.
    const scrollToTarget = () => {
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return true
      }
      return false
    }

    if (scrollToTarget()) return

    const rafId = requestAnimationFrame(() => {
      if (scrollToTarget()) return
      // Final fallback: landing page sections may still be mounting.
      const timeoutId = setTimeout(scrollToTarget, 100)
      // Note: we don't return the cleanup from inside rAF; the timeout
      // is short enough that a re-navigation before it fires is fine.
      // Stored on window only if we needed to clear it — we don't.
      void timeoutId
    })

    return () => cancelAnimationFrame(rafId)
  }, [pathname, hash])

  return null
}

export default ScrollToTop
